import React, { useEffect, useState } from 'react';
import '../Styles/Proxy.scss';

// Visual Assets
import ProxyPNG from '../Styles/proxy.png';
import ProxyJourneyMap from '../Styles/proxy/proxy_journey_map.png';
import ProxyPersona from '../Styles/proxy/proxy_persona.png';
import ProxySitemap from '../Styles/proxy/proxy_sitemap.png';
import ProxyDigitalFrames from '../Styles/proxy/proxy_digital_frames.png';
import ProxyDigitalFramesMobiles from '../Styles/proxy/proxy_digital_frames_mobiles.png';
import ProxyLoFi from '../Styles/proxy/proxy_lo-fi.png';

// Mockup Screenshots
import ProxyPrototype_04 from '../Styles/proxy/proxy_04.png';
import ProxyPrototype_05 from '../Styles/proxy/proxy_05.png';
import ProxyPrototype_010 from '../Styles/proxy/proxy_010.png';
import ProxyPrototype_014 from '../Styles/proxy/proxy_014.png';
import ProxyPrototype_017 from '../Styles/proxy/proxy_017.png';

// Data Configuration Constants
const PAIN_POINTS = [
  {
    title: 'Pain Point 1',
    description: 'Difficulty identifying trustworthy websites, especially with convincing fake reviews.'
  },
  {
    title: 'Pain Point 2',
    description: 'Fear of financial loss due to a lack of secure payment options.'
  },
  {
    title: 'Pain Point 3',
    description: 'Frustration with unresponsive or unhelpful customer support when problems occur.'
  }
];

const STUDY_PARAMETERS = [
  { label: 'Study Type:', value: 'Unmoderated usability study' },
  { label: 'Location:', value: 'United States, remote' },
  { label: 'Participants:', value: '5 participants' },
  { label: 'Length:', value: '20-30 minutes' }
];

const STUDY_FINDINGS = [
  {
    title: 'Finding 1',
    subtitle: 'Verified Seller Badges',
    description: 'Users found badges reassuring and were more likely to trust products with visible verification.'
  },
  {
    title: 'Finding 2',
    subtitle: 'Secure Payment Options',
    description: 'Users appreciated the inclusion of secure payment methods and fraud protection labels.'
  },
  {
    title: 'Finding 3',
    subtitle: 'Trust Building Content',
    description: 'Participants valued scam prevention tips, though some felt the info could be more prominent.'
  }
];

const ACCESSIBILITY_ITEMS = [
  {
    title: 'Clear navigation',
    description: 'Include clear visual and text cues for navigation elements.'
  },
  {
    title: 'Readable text',
    description: 'Ensure high-contrast text for readability, especially for visually impaired users.'
  },
  {
    title: 'Screen reader compatibility',
    description: 'Ensure all images and icons have descriptive alt text.'
  }
];

const NEXT_STEPS = [
  {
    title: 'Refine Insights',
    description: 'Refine insights and objectives using research findings to define clear metrics and priorities.'
  },
  {
    title: 'Build Key Elements',
    description: 'Build key elements like verified seller badges, secure payment systems, and an intuitive help center.'
  },
  {
    title: 'Test & Iterate',
    description: 'Conduct usability and accessibility testing, gathering user feedback to refine the platform.'
  }
];

const MOCKUP_IMAGES = [
  ProxyPrototype_05,
  ProxyPrototype_010,
  ProxyPrototype_014,
  ProxyPrototype_04
];

const Proxy = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollButton(window.scrollY > 400);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="main-proxy-container">
      {/* Hero / Overview Section */}
      <section className="case-study-section header-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Project Overview</strong></h1>
            <div className="main-proxy-product">
              <h2><strong>The Product</strong></h2>
              <p>
                The project aims to understand and improve the online shopping experience by identifying pain points, particularly when users encounter fraudulent transactions. Through this, we seek to create trust, enhance user satisfaction, and develop preventive solutions.
              </p>
              <div className="project">
                <h2><strong>Project Duration</strong></h2>
                <p>Nov 2024 to Dec 2024</p>
              </div>
            </div>
          </div>
          <div className="right-section">
            <div className="img-wrapper shadow-effect">
              <img src={ProxyPNG} alt="Proxy Project Overview Preview" />
            </div>
          </div>
        </div>
      </section>

      {/* Problem & Goal Section */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content">
          <div className="left-section">
            <div className="main-proxy-product text-changer">
              <h2 className="text-changer"><strong>The Problem</strong></h2>
              <p className="text-changer">
                This project aimed to address the challenges of online shopping fraud, including the difficulty of identifying trustworthy websites, the absence of secure payment options, and inadequate post-purchase support. By tackling these issues, the goal was to enhance user trust, safety, and satisfaction in e-commerce platforms.
              </p>
            </div>
          </div>
          <div className="right-section text-changer">
            <div className="goal-section">
              <h2><strong>The Goal</strong></h2>
              <p>
                The goal of this project is to create a secure and user-friendly online shopping platform that builds trust and confidence by addressing common pain points, such as fraud prevention, accessible support, and transparent transaction processes. It aims to empower users with tools and resources for a safer shopping experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Role & Responsibilities */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <div className="main-proxy-product">
              <h2><strong>My Role</strong></h2>
              <p>
                As the UX designer leading the project, I took ownership of every aspect of the design process—from user research and prototyping to testing and refinement. Leveraging Replit AI, I streamlined development, ensured efficiency, and introduced innovative solutions aligning with project goals.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="responsibilities-card">
              <h2><strong>Responsibilities</strong></h2>
              <p>
                Conducting interviews, wireframing, low/high-fidelity prototyping, conducting usability studies, accounting for accessibility, iterating on designs, and responsive design.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Divider: Understanding the User */}
      <section className="case-study-section bg-changer process-divider">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Understanding The User</strong></h1>
          </div>
          <div className="right-section">
            <div className="text-changer process-tags">
              <h2><strong>User Research</strong></h2>
              <h2><strong>Personas</strong></h2>
              <h2><strong>Problem Statements</strong></h2>
              <h2><strong>User Journey Maps</strong></h2>
            </div>
          </div>
        </div>
      </section>

      {/* User Research Summary */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section full-width">
            <h1><strong>User Research: Summary</strong></h1>
            <div className="main-proxy-product">
              <p>
                Research involved engaging a diverse group of participants, including scammed online shoppers, cautious buyers, frequent e-commerce users, and first-time shoppers. Through interviews, surveys, and usability testing, key pain points were identified, including fraudulent website identification, payment security, and post-purchase support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content text-changer">
          <div className="left-section full-width">
            <h1><strong>User Research: Pain Points</strong></h1>
            <div className="pain-points-grid">
              {PAIN_POINTS.map((point, index) => (
                <div className="main-proxy-pain-point" key={index}>
                  <h2><strong>{point.title}</strong></h2>
                  <p>{point.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Persona Section */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Persona: David</strong></h1>
            <div className="main-proxy-product">
              <h2><strong>Problem Statement</strong></h2>
              <p>
                David struggles to trust online stores due to misleading designs, insecure payments, and poor support. He needs a safer, transparent shopping experience to regain confidence in e-commerce.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section persona-card">
              <h2><strong>David Smith</strong></h2>
              <div className="img-wrapper">
                <img src={ProxyPersona} alt="User Persona: David Smith" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Journey Map */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>User Journey Map</strong></h1>
            <div className="main-proxy-product">
              <p>
                I created a user journey map of David’s experience using the site to help identify potential pain points and opportunities for improvement.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section journey-wrapper">
              <img src={ProxyJourneyMap} alt="User Journey Map Diagram" />
            </div>
          </div>
        </div>
      </section>

      {/* Process Divider: Starting the Design */}
      <section className="case-study-section bg-changer process-divider">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Starting The Design</strong></h1>
          </div>
          <div className="right-section">
            <div className="text-changer process-tags">
              <h2><strong>Sitemap</strong></h2>
              <h2><strong>Digital Wireframes</strong></h2>
              <h2><strong>Low-fidelity Prototype</strong></h2>
              <h2><strong>Usability Studies</strong></h2>
            </div>
          </div>
        </div>
      </section>

      {/* Sitemap */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Sitemap</strong></h1>
            <div className="main-proxy-product">
              <p>
                I built user-focused flows to ensure that my personas can successfully complete their key objectives while reducing existing pain points.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section img-wrapper">
              <img src={ProxySitemap} alt="Sitemap Diagram" />
            </div>
          </div>
        </div>
      </section>

      {/* Digital Wireframes */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1 className="text-changer"><strong>Digital Wireframes</strong></h1>
            <div className="main-proxy-product">
              <p className="text-changer">
                Using wireframes, I put my ideas on digital media first and then transitioned into high-fidelity designs. After iterations, these wireframes best represented the intended user flow.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section img-wrapper">
              <img src={ProxyDigitalFrames} alt="Digital Wireframes Overview" />
            </div>
          </div>
        </div>
      </section>

      {/* Digital Wireframes Responsive */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Digital Wireframes (Screen Variations)</strong></h1>
            <div className="main-proxy-product">
              <p>
                I also designed wireframes for additional screen sizes to ensure the site would be fully responsive.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section img-wrapper">
              <img src={ProxyDigitalFramesMobiles} alt="Mobile Digital Wireframe Variations" />
            </div>
          </div>
        </div>
      </section>

      {/* Low-fidelity Prototype */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1 className="text-changer"><strong>Low-fidelity Prototype</strong></h1>
            <div className="main-proxy-product">
              <p className="text-changer">
                I created a low-fidelity prototype from the user flow diagram to test functionality before final production, ensuring accessibility and ease of use.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section img-wrapper">
              <img src={ProxyLoFi} alt="Low-Fidelity Prototype Flow" />
            </div>
          </div>
        </div>
      </section>

      {/* Usability Study Parameters */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section full-width">
            <h1><strong>Usability Study: Parameters</strong></h1>
            <div className="parameters-grid">
              {STUDY_PARAMETERS.map((param, index) => (
                <div className="main-proxy-pain-point" key={index}>
                  <h2><strong>{param.label}</strong></h2>
                  <p>{param.value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Usability Study Findings */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content text-changer">
          <div className="left-section full-width">
            <h1 className="text-changer"><strong>Usability Study: Findings</strong></h1>
            <div className="findings-grid">
              {STUDY_FINDINGS.map((finding, index) => (
                <div className="main-proxy-pain-point" key={index}>
                  <h2 className="text-changer"><strong>{finding.title}</strong></h2>
                  <p className="text-changer">
                    <strong>{finding.subtitle}:</strong> {finding.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Divider: Refining The Design */}
      <section className="case-study-section process-divider">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Refining The Design</strong></h1>
          </div>
          <div className="right-section">
            <div className="right-user-section process-tags">
              <h2><strong>Mockups</strong></h2>
              <h2><strong>High-fidelity Prototype</strong></h2>
              <h2><strong>Accessibility</strong></h2>
            </div>
          </div>
        </div>
      </section>

      {/* Mockup Gallery */}
      <section className="case-study-section gallery-section">
        <div className="main-proxy-content full-width-content">
          <div className="left-section full-width">
            <h1><strong>Mockups: Screen Showcase</strong></h1>
            <div className="mocks">
              {MOCKUP_IMAGES.map((src, index) => (
                <div className="mock-card" key={index}>
                  <img src={src} alt={`High-fidelity mockup showcase ${index + 1}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* High-Fidelity Prototype */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>High-Fidelity Prototype</strong></h1>
            <div className="main-proxy-product">
              <p className="text-changer">
                After finalizing the low-fidelity prototype, I created final designs focused on simplicity and intuition. Red was chosen as the primary brand accent color to represent confidence, security, and decisive user action.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section img-wrapper highlight-border">
              <img src={ProxyPrototype_017} alt="High-fidelity Prototype Presentation" />
            </div>
          </div>
        </div>
      </section>

      {/* Accessibility Considerations */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section full-width">
            <h1><strong>Accessibility Considerations</strong></h1>
            <div className="accessibility-grid">
              {ACCESSIBILITY_ITEMS.map((item, index) => (
                <div className="main-proxy-pain-point" key={index}>
                  <div className="badge">{index + 1}</div>
                  <h2><strong>{item.title}</strong></h2>
                  <p>{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Divider: Going Forward */}
      <section className="case-study-section bg-changer process-divider">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Going Forward</strong></h1>
          </div>
          <div className="right-section">
            <div className="text-changer process-tags">
              <h2><strong>Takeaways</strong></h2>
              <h2><strong>Next Steps</strong></h2>
            </div>
          </div>
        </div>
      </section>

      {/* Takeaways */}
      <section className="case-study-section">
        <div className="main-proxy-content">
          <div className="left-section">
            <h1><strong>Takeaways</strong></h1>
            <div className="main-proxy-product">
              <h2><strong>Impact</strong></h2>
              <p>
                The key takeaway is the importance of building trust, ensuring security, and enhancing user experience in e-commerce. Users heavily value clear verification, transparent payments, and support.
              </p>
            </div>
          </div>
          <div className="right-section">
            <div className="right-user-section takeaway-card">
              <h2><strong>What I Learned</strong></h2>
              <p>
                Users require explicit visual trust signals (e.g., badges, secure payment guarantees) to feel confident during checkout.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Next Steps */}
      <section className="case-study-section bg-changer">
        <div className="main-proxy-content text-changer">
          <div className="left-section full-width">
            <h1 className="text-changer"><strong>Next Steps</strong></h1>
            <div className="next-steps-grid">
              {NEXT_STEPS.map((step, index) => (
                <div className="main-proxy-pain-point" key={index}>
                  <div className="badge">{index + 1}</div>
                  <h2 className="text-changer"><strong>{step.title}</strong></h2>
                  <p className="text-changer">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Back to Top Button */}
      {showScrollButton && (
        <button className="scroll-to-top" onClick={scrollToTop} aria-label="Scroll to top">
          ↑ Top
        </button>
      )}
    </div>
  );
};

export default Proxy;